/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import api from '@utils/axios';
import { translate } from '@utils/i18n';
import toast from 'react-hot-toast';
import { apiInternalVisitPath, checkAvailableTechniciansPath } from 'routes';
import { Duration } from 'luxon';
import { Button } from '@ui';
import LoadableButton from '@components/LoadableButton/LoadableButton';
import TimeInput from '@components/TimeInput/TimeInput';
import Select from '@components/Select/Select';
import { Dialog } from '@progress/kendo-react-dialogs';
import InputTextArea, { StyleVariant } from '@components/InputTextArea/InputTextArea';
import DateInput from '@components/DateInput/DateInput';
import VisitPriorityDropdown from '@components/Dropdowns/VisitPriorityDropdown';
import { convertUTCToLocalDate, convertLocalDateToUTC } from '@utils/dateUtils';
import { TimeZoneContext } from '@contexts/timezone-context';
import { TenantContext } from '@contexts/tenant-context';
import FormTypeSelector from './components/FormTypeSelector';
import TextInput from './components/TextInput';
import { getDMHfromMins } from '../../../utils/getDatetimeFormat';
import { VisitPriority } from './constants';

const namespace = 'features.work_orders.visits_section';

export default function EditVisitModal({
  visit = {},
  isOpen,
  woId,
  onClose,
  techOptions,
  vendorOptions,
  statusesOptions,
  onEditComplete,
  defaultTechnicianId,
  defaultSubcontractorId,
  priorityOptions,
  isSubcontractorsEnabled,
  timeZoneOptions
}) {
  const { timeZonesMapping } = useContext(TimeZoneContext);
  const { tenant } = useContext(TenantContext);
  const [loading, setIsLoading] = useState(false);
  const [isVendor, setIsVendor] = useState(false);
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [checkAvailableNotification, setCheckAvailableNotification] = useState({ message: '', data: null });
  const { register, handleSubmit, control, setValue, reset, watch } = useForm({
    defaultValues: {
      id: visit?.id,
      technicianId: visit?.technician_id || defaultTechnicianId || '',
      subcontractorId: visit?.subcontractor_id || defaultSubcontractorId || '',
      status: visit?.visit_status,
      start: convertUTCToLocalDate(visit?.start, timeZonesMapping[visit?.time_zone]),
      days: getDMHfromMins(visit?.duration).days,
      hours: getDMHfromMins(visit?.duration).hours,
      minutes: getDMHfromMins(visit?.duration).minutes,
      workPerformed: visit?.work_performed,
      workDate: visit?.work_date ? convertUTCToLocalDate(visit?.work_date, timeZonesMapping[visit?.time_zone]) : null,
      visitNotes: visit?.visit_notes,
      notesToVendor: visit?.notes,
      followUp: visit?.follow_up,
      poNumber: visit?.subcontractor_po_number,
      billingLimit: visit?.subcontractor_billing_limit,
      priority: visit?.priority,
      priorityNote: visit?.priority_note,
      timeZone: visit?.time_zone
    }
  });

  const priority = watch('priority');
  const isHighPriority = priority === VisitPriority.HIGH;

  useEffect(() => {
    setIsNoteVisible(Boolean(visit?.visit_notes));
  }, [visit]);

  useEffect(() => {
    let controller = new AbortController();

    const subscription = watch((value, { name, type }) => {
      if (type !== 'change') return;

      if (!['minutes', 'hours', 'days', 'technicianId', 'start'].includes(name)) return;
      if (!parseInt(value.technicianId, 10)) {
        setCheckAvailableNotification({ message: '', data: null });
        return;
      }

      controller.abort();
      controller = new AbortController();

      api
        .get(checkAvailableTechniciansPath({ format: 'json' }), {
          signal: controller.signal,
          params: {
            default_object_name: 'Visit',
            default_object_id: value.id,
            technician_id: value.technicianId,
            scheduled_date: convertLocalDateToUTC(value.start, timeZonesMapping[value.timeZone]),
            time_zone: value.timeZone,
            duration: Duration.fromObject({
              days: value.days,
              hours: value.hours,
              minutes: value.minutes
            }).as('minutes')
          }
        })
        .then(() => setCheckAvailableNotification({ message: '', data: null }))
        .catch((error) =>
          setCheckAvailableNotification({
            message: error?.response?.data?.message,
            data: error?.response?.data?.data
          })
        );
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = (newVisitInfo) => {
    if (woId) return onSubmitOnPersisted(newVisitInfo);

    return onSubmitOnNewRecord(newVisitInfo);
  };

  const onSubmitOnNewRecord = (newVisitInfo) => {
    onEditComplete({
      id: newVisitInfo.id,
      start: convertLocalDateToUTC(newVisitInfo.start, timeZonesMapping[newVisitInfo.timeZone]),
      duration: Number(newVisitInfo.days) * 1440 + Number(newVisitInfo.hours * 60) + Number(newVisitInfo.minutes),
      technician_id: !isVendor ? Number(newVisitInfo.technicianId) || null : null,
      notes: isVendor ? newVisitInfo.notesToVendor : undefined,
      visit_status: newVisitInfo.status ? newVisitInfo.status : undefined,
      subcontractor_id: isVendor ? Number(newVisitInfo.subcontractorId) || null : null,
      subcontractor_po_number: newVisitInfo.poNumber ? newVisitInfo.poNumber : undefined,
      subcontractor_billing_limit: newVisitInfo.billingLimit ? newVisitInfo.billingLimit : undefined,
      work_performed: newVisitInfo.workPerformed || undefined,
      work_date: newVisitInfo.workDate
        ? convertLocalDateToUTC(newVisitInfo.workDate, timeZonesMapping[newVisitInfo.timeZone])
        : undefined,
      visit_notes: newVisitInfo.visitNotes || undefined,
      follow_up: newVisitInfo.followUp || undefined,
      assignee_type: isVendor ? 'subcontractor' : 'technician',
      priority: newVisitInfo.priority,
      priority_note: newVisitInfo.priority === VisitPriority.HIGH ? newVisitInfo.priorityNote : null,
      time_zone: newVisitInfo.timeZone
    });
  };

  const onSubmitOnPersisted = async (newVisitInfo) => {
    try {
      setIsLoading(true);
      const visitInfo = {
        scheduled_date: convertLocalDateToUTC(newVisitInfo.start, timeZonesMapping[newVisitInfo.timeZone]),
        duration: Number(newVisitInfo.days) * 1440 + Number(newVisitInfo.hours * 60) + Number(newVisitInfo.minutes),
        technician_id: !isVendor ? newVisitInfo.technicianId ?? null : null,
        work_order_id: visit.work_order_id,
        notes: isVendor ? newVisitInfo.notesToVendor : undefined,
        work_performed: newVisitInfo.workPerformed,
        work_date: newVisitInfo.workDate
          ? convertLocalDateToUTC(newVisitInfo.workDate, timeZonesMapping[newVisitInfo.timeZone])
          : null,
        visit_notes: newVisitInfo.visitNotes,
        follow_up: newVisitInfo.followUp,
        visit_status: newVisitInfo.status ? newVisitInfo.status : undefined,
        subcontractor_id: isVendor ? newVisitInfo.subcontractorId ?? null : null,
        subcontractor_po_number: newVisitInfo.poNumber && isVendor ? newVisitInfo.poNumber : undefined,
        subcontractor_billing_limit: newVisitInfo.billingLimit && isVendor ? newVisitInfo.billingLimit : undefined,
        assignee_type: isVendor ? 'subcontractor' : 'technician',
        priority: newVisitInfo.priority,
        priority_note: newVisitInfo.priority === VisitPriority.HIGH ? newVisitInfo.priorityNote : null,
        time_zone_override: newVisitInfo.timeZone
      };

      const { data: res } = await api.patch(apiInternalVisitPath({ id: visit.id, format: 'json' }), {
        visit: visitInfo
      });
      setIsLoading(false);
      onEditComplete(res.data);
      reset();
      setIsVendor(false);
    } catch (e) {
      toast.error(translate('edit_error', { namespace }), {
        position: 'bottom-right',
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  useEffect(() => {
    setIsVendor(visit?.assignee_type === 'subcontractor');
    setValue('start', convertUTCToLocalDate(visit?.start, timeZonesMapping[visit?.time_zone]));
    setValue('days', getDMHfromMins(visit?.duration).days);
    setValue('hours', getDMHfromMins(visit?.duration).hours);
    setValue('minutes', getDMHfromMins(visit?.duration).minutes);
    setValue('technicianId', visit?.technician_id || defaultTechnicianId);
    setValue('subcontractorId', visit?.subcontractor_id || defaultSubcontractorId);
    setValue('status', visit?.visit_status);
    setValue('notesToVendor', visit?.notes || '');
    setValue('workPerformed', visit?.work_performed || '');
    setValue(
      'workDate',
      visit?.work_date ? convertUTCToLocalDate(visit?.work_date, timeZonesMapping[visit?.time_zone]) : ''
    );
    setValue('visitNotes', visit?.visit_notes || '');
    setValue('followUp', visit?.follow_up || '');
    setValue('poNumber', visit?.subcontractor_po_number || '');
    setValue('billingLimit', visit?.subcontractor_billing_limit || '');
    setValue('priority', visit?.priority);
    setValue('priorityNote', visit?.priority_note || '');
    setValue('timeZone', visit?.time_zone || '');
    setValue('id', visit?.id ?? '');
  }, [visit]);

  const onClickCancel = () => {
    onClose();
    reset();
    setIsVendor(false);
  };

  const onChangeAsigneeType = ({ isVendor: newIsVendor }) => {
    setIsVendor(newIsVendor);
  };

  const technicianOptions = techOptions.map(([name, id]) => ({ label: name, value: id }));
  const subcontractorOptions = useMemo(
    () => (vendorOptions[visit?.id] || vendorOptions.default).map(([name, id]) => ({ label: name, value: id })),
    [visit]
  );

  const statuses = statusesOptions.map(([name, id]) => ({ label: name, value: id }));
  const priorities = priorityOptions.map(([name, id]) => ({ label: name, value: id }));
  const timeZones = timeZoneOptions.map(([name, id]) => ({ label: name, value: id }));

  if (!isOpen) return null;

  return (
    <Dialog
      className="qmb-dialog custom-dialog-scroll"
      title={translate('edit_visit', { namespace })}
      onClose={onClose}
      width={600}>
      <section className="qmb-modal__body visitModal">
        <form className="modalForm" onSubmit={handleSubmit(onSubmit)}>
          {isSubcontractorsEnabled && (
            <div className="formSection">
              <FormTypeSelector isVendor={isVendor} onChange={onChangeAsigneeType} />
            </div>
          )}
          <div className="formSection equalChildrens">
            {!isVendor && (
              <Select
                {...register('technicianId')}
                id="technicianId"
                noOptionLabel={translate('unassigned', { namespace })}
                label={translate('technician', { namespace })}
                options={technicianOptions}
              />
            )}
            {isVendor && (
              <Select
                {...register('subcontractorId')}
                id="subcontractorId"
                noOptionLabel={translate('unassigned', { namespace })}
                label={translate('subcontractor', { namespace })}
                options={subcontractorOptions}
              />
            )}
            <Select {...register('status')} label={translate('status', { namespace })} id="status" options={statuses} />
          </div>
          {isVendor && (
            <div className="formSection">
              <div className="equalChildrens">
                <Controller
                  name="poNumber"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      id="poNumber"
                      value={field.value}
                      name={field.name}
                      onChange={field.onChange}
                      label={`${translate('po', { namespace })} #`}
                    />
                  )}
                />
                <Controller
                  name="billingLimit"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      id="billingLimit"
                      value={field.value}
                      name={field.name}
                      onChange={field.onChange}
                      type="number"
                      min={0}
                      label={translate('billing_limit', { namespace })}
                    />
                  )}
                />
              </div>
            </div>
          )}

          {tenant?.flags?.hasMultiTimezone && (
            <div className="formSection">
              <div className="equalChildrens">
                <Select
                  {...register('timeZone')}
                  id="timeZone"
                  label={translate('time_zone', { namespace })}
                  options={timeZones}
                  required
                />
                <div />
              </div>
            </div>
          )}

          <div className="formSection">
            <div className="equalChildrens">
              <Controller
                name="start"
                control={control}
                render={({ field }) => (
                  <DateInput
                    id="start"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    label={translate('scheduled_date', { namespace })}
                  />
                )}
              />
              <Controller
                name="start"
                control={control}
                render={({ field }) => (
                  <TimeInput
                    id="start"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    label={translate('start_time', { namespace })}
                  />
                )}
              />
            </div>
            <div className="equalChildrens">
              <span>{translate('duration', { namespace })}:</span>
              <Controller
                name="days"
                control={control}
                render={({ field }) => (
                  <TextInput
                    id="days"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    label={translate('days', { namespace: 'features.commons' })}
                    type="number"
                    min={0}
                  />
                )}
              />
              <Controller
                name="hours"
                control={control}
                render={({ field }) => (
                  <TextInput
                    id="hours"
                    type="number"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    min={0}
                    label={translate('hours', { namespace: 'features.commons' })}
                  />
                )}
              />
              <Controller
                name="minutes"
                control={control}
                render={({ field }) => (
                  <TextInput
                    id="minutes"
                    type="number"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    min={0}
                    label={translate('minutes', { namespace: 'features.commons' })}
                  />
                )}
              />
            </div>
          </div>
          {!isVendor && checkAvailableNotification.message && (
            <div style={{ margin: 'auto' }}>
              {checkAvailableNotification.message}
              <br />
              {checkAvailableNotification.data && (
                <a href={checkAvailableNotification.data.link} target="_blank" rel="noreferrer">
                  {checkAvailableNotification.data.text_link}
                </a>
              )}
            </div>
          )}
          {!isNoteVisible && (
            <Button variant="highlighted" onClick={() => setIsNoteVisible(true)}>
              {translate('add_visit_note', { namespace })}
            </Button>
          )}
          {isNoteVisible && (
            <div className="formSection vertical visit-notes-input-wrapper">
              <Controller
                name="visitNotes"
                control={control}
                render={({ field }) => (
                  <InputTextArea
                    className="visit-note-field"
                    label={translate('visit_notes', { namespace })}
                    value={field.value}
                    name={field.name}
                    buttonControlled={false}
                    variant={StyleVariant.Light}
                    onChange={field.onChange}
                  />
                )}
              />
              <Button
                className="close-note-button close-note-button--kendo"
                aria-label={translate('close', { namespace: 'features.commons' })}
                onClick={() => {
                  setIsNoteVisible(false);
                  setValue('visitNotes', '');
                }}>
                &times;
              </Button>
            </div>
          )}
          <div className="formSection">
            <div className="equalChildrens">
              <Controller
                name="workDate"
                control={control}
                render={({ field }) => (
                  <DateInput
                    id="work_date"
                    value={field.value}
                    name={field.name}
                    onChange={field.onChange}
                    label={translate('work_date', { namespace })}
                  />
                )}
              />
              <div />
            </div>
          </div>
          <div className="formSection equalChildrens vertical">
            <Controller
              name="workPerformed"
              control={control}
              render={({ field }) => (
                <InputTextArea
                  label={translate('work_performed', { namespace })}
                  value={field.value}
                  name={field.name}
                  buttonControlled={false}
                  variant={StyleVariant.Light}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="followUp"
              control={control}
              render={({ field }) => (
                <InputTextArea
                  label={translate('follow_up', { namespace })}
                  value={field.value}
                  name={field.name}
                  buttonControlled={false}
                  variant={StyleVariant.Light}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="form__set--vertical--compact">
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <VisitPriorityDropdown
                  visitPriorityList={priorities}
                  selectedValue={field.value}
                  onChange={field.onChange}
                  disabled={false}
                />
              )}
            />

            {isHighPriority && (
              <Controller
                name="priorityNote"
                control={control}
                render={({ field }) => (
                  <InputTextArea
                    label={translate('priority_note', { namespace })}
                    value={field.value}
                    name={field.name}
                    buttonControlled={false}
                    variant={StyleVariant.Light}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </div>
          <div className="modalActionContainer">
            <Button onClick={onClickCancel}>{translate('cancel', { namespace: 'features.commons' })}</Button>
            <LoadableButton type="submit" loading={loading} variant="primary">
              {translate('save', { namespace: 'features.commons' })}
            </LoadableButton>
          </div>
        </form>
      </section>
    </Dialog>
  );
}

EditVisitModal.propTypes = {
  woId: PropTypes.number,
  onEditComplete: PropTypes.func.isRequired,
  visit: PropTypes.object,
  onEditVisit: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  techOptions: PropTypes.array,
  priorityOptions: PropTypes.array,
  timeZoneOptions: PropTypes.array,
  vendorOptions: PropTypes.object,
  statusesOptions: PropTypes.array,
  defaultTechnicianId: PropTypes.number,
  defaultSubcontractorId: PropTypes.number,
  isSubcontractorsEnabled: PropTypes.bool.isRequired
};

EditVisitModal.defaultProps = {
  woId: null,
  visit: {},
  onEditVisit: () => {},
  isOpen: false,
  onClose: () => {},
  techOptions: [],
  priorityOptions: [],
  timeZoneOptions: [],
  vendorOptions: {},
  statusesOptions: [],
  defaultTechnicianId: null,
  defaultSubcontractorId: null
};
